import { generateClient } from 'aws-amplify/api';

const client = generateClient();

const AUTO_SAVE_INTERVAL = parseInt(process.env.REACT_APP_GAME_SAVE_INTERVAL, 10);
const MAX_CHECKPOINTS = parseInt(process.env.REACT_APP_MAX_CHECKPOINTS, 10);

export class SaveStateManager {
  constructor(gameId, characterId) {
    this.gameId = gameId;
    this.characterId = characterId;
    this.autoSaveInterval = AUTO_SAVE_INTERVAL || 5 * 60 * 1000; // 5 minutes default
    this.autoSaveTimer = null;
    this.lastSaveTime = null;
  }

  static createInitialState(gameId, character) {
    return {
      metadata: {
        lastSaved: new Date().toISOString(),
        sceneName: 'intro',
        sceneId: 'intro_01',
        turnCount: 0,
        gameModuleId: gameId,
      },
      characterState: {
        id: character.id,
        checkpoints: [],
        inventory: [],
        currentHealth: character.maxHealth,
        experience: 0
      },
      storyState: {
        discoveredLocations: new Set(),
        completedObjectives: new Set(),
        acquiredItems: [],
        storyFlags: new Set(),
        keyDecisions: []
      },
      combatState: {
        inCombat: false,
        enemies: [],
        conditions: new Map()
      }
    };
  }

  // Save game state
  async saveGameState(state) {
    try {
      const now = new Date().toISOString();
      const serializedState = this.serializeState({
        ...state,
        metadata: {
          ...state.metadata,
          lastSaved: now
        }
      });

      // Update game state
      await client.graphql({
        query: `
          mutation UpdateGameState($input: UpdateGameStateInput!) {
            updateGameState(input: $input) {
              id
              currentScene
              state
            }
          }
        `,
        variables: {
          input: {
            id: state.gameStateId,
            currentScene: state.metadata.sceneName,
            state: JSON.stringify(serializedState)
          }
        }
      });

      // Update character game state
      await client.graphql({
        query: `
          mutation UpdateCharacterGameState($input: UpdateCharacterGameStateInput!) {
            updateCharacterGameState(input: $input) {
              id
              individualState
              currentHealth
              experience
            }
          }
        `,
        variables: {
          input: {
            id: state.characterGameStateId,
            individualState: JSON.stringify(serializedState),
            currentHealth: state.characterState.currentHealth,
            experience: state.characterState.experience
          }
        }
      });

      this.lastSaveTime = now;
      return true;
    } catch (error) {
      console.error('Error saving game state:', error);
      throw error;
    }
  }

  // Load game state
  async loadGameState() {
    try {
      const { data } = await client.graphql({
        query: `
          query GetCharacterGameState($characterId: ID!) {
            getCharacterGameState(characterId: $characterId) {
              id
              gameStateId
              individualState
              currentHealth
              experience
              gameState {
                id
                currentScene
                state
              }
            }
          }
        `,
        variables: {
          characterId: this.characterId
        }
      });

      if (!data.getCharacterGameState) {
        throw new Error('No game state found');
      }

      const savedState = JSON.parse(data.getCharacterGameState.individualState);
      return this.rehydrateState(savedState);

    } catch (error) {
      console.error('Error loading game state:', error);
      throw error;
    }
  }

  // Create a checkpoint
  async createCheckpoint(state, checkpointName) {
    try {
      const checkpoint = {
        name: checkpointName,
        timestamp: new Date().toISOString(),
        state: this.serializeState(state)
      };

      const checkpoints = [...state.characterState.checkpoints, checkpoint]
        .slice(-MAX_CHECKPOINTS); // Keep only the most recent checkpoints

      const newState = {
        ...state,
        characterState: {
          ...state.characterState,
          checkpoints
        }
      };

      await this.saveGameState(newState);
      return checkpoint;
    } catch (error) {
      console.error('Error creating checkpoint:', error);
      throw error;
    }
  }

  // Load from checkpoint
  async loadCheckpoint(checkpointIndex) {
    try {
      const gameState = await this.loadGameState();
      const checkpoint = gameState.characterState.checkpoints[checkpointIndex];
      
      if (!checkpoint) {
        throw new Error('Checkpoint not found');
      }

      const mergedState = {
        ...this.rehydrateState(checkpoint.state),
        metadata: {
          ...checkpoint.state.metadata,
          lastSaved: new Date().toISOString()
        },
        characterState: {
          ...checkpoint.state.characterState,
          checkpoints: gameState.characterState.checkpoints
        }
      };

      await this.saveGameState(mergedState);
      return mergedState;
    } catch (error) {
      console.error('Error loading checkpoint:', error);
      throw error;
    }
  }

  // Utility methods
  serializeState(state) {
    return {
      ...state,
      storyState: {
        ...state.storyState,
        discoveredLocations: Array.from(state.storyState.discoveredLocations),
        completedObjectives: Array.from(state.storyState.completedObjectives),
        storyFlags: Array.from(state.storyState.storyFlags)
      },
      combatState: {
        ...state.combatState,
        conditions: Array.from(state.combatState.conditions.entries())
      }
    };
  }

  rehydrateState(state) {
    return {
      ...state,
      storyState: {
        ...state.storyState,
        discoveredLocations: new Set(state.storyState.discoveredLocations),
        completedObjectives: new Set(state.storyState.completedObjectives),
        storyFlags: new Set(state.storyState.storyFlags)
      },
      combatState: {
        ...state.combatState,
        conditions: new Map(state.combatState.conditions)
      }
    };
  }

  startAutoSave(saveCallback) {
    this.stopAutoSave();
    this.autoSaveTimer = setInterval(saveCallback, this.autoSaveInterval);
  }

  stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  getTimeSinceLastSave() {
    if (!this.lastSaveTime) return null;
    return new Date() - new Date(this.lastSaveTime);
  }

  dispose() {
    this.stopAutoSave();
  }
}

export const SaveStateEvents = {
  SAVE_STARTED: 'saveStarted',
  SAVE_COMPLETED: 'saveCompleted',
  SAVE_FAILED: 'saveFailed',
  CHECKPOINT_CREATED: 'checkpointCreated',
  STATE_LOADED: 'stateLoaded'
};

export const saveStateEmitter = new EventTarget();